/**
 * What this nursery is offering, and the contract behind it.
 *
 * Three things this screen has to be unambiguous about.
 *
 * THE PRICE IS PRIVATE. The tree price set here is never shown to another
 * nursery, and a grower sees one combined total with the licence fees folded
 * in. That is a deliberate choice rather than an oversight, and the page says
 * so — a nursery entering a number needs to know who can read it.
 *
 * THE SUPPLY CONTRACT IS THE NURSERY'S. Supplying the trees is a contract
 * between the nursery and the grower; Factree licenses the thing being
 * propagated and is not a party to the sale. A nursery either attaches its
 * terms of sale here, or acknowledges that it handles them outside Factree —
 * which is recorded against the name of whoever ticked it, with the exact
 * wording they were shown. It cannot do neither.
 *
 * THE MATERIAL IS DECLARED. Before an offer goes on sale the nursery records
 * where its scion material comes from — the double check that the trees sold
 * under the licence are the cultivar the licence is for.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Sprout, Loader2, Plus, Upload, FileText, AlertCircle, Lock,
  Calendar, Truck, X, ExternalLink, ShieldCheck,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import CultivarPicker, { type PickedCultivar } from '../../components/orchard/CultivarPicker';
import MaterialSourcesEditor from '../../components/cultivar/MaterialSourcesEditor';
import cultivarService, {
  type NurseryOffer,
  type NurseryOfferInput,
  type NurseryTermsDocument,
  type PropagatorTerms,
} from '../../services/cultivar';
import {
  MATERIAL_KIND_LABELS,
  blankMaterialSource,
  materialSourcesProblem,
  type MaterialSourceInput,
} from '../../types/propagation';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatLocalDateInTz, formatDateInTz } from '../../utils/timezone';

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-neutral-100 text-neutral-700 border-neutral-200',
  active: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  closed: 'bg-amber-50 text-amber-900 border-amber-200',
  withdrawn: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};

function money(v: string | number | null | undefined, currency = 'AUD'): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return '—';
  return `${currency} ${n.toFixed(2)}`;
}

function depositLabel(offer: NurseryOffer): string {
  if (offer.deposit_type === 'none' || offer.deposit_value === null) return 'No deposit';
  const n = typeof offer.deposit_value === 'string'
    ? parseFloat(offer.deposit_value)
    : offer.deposit_value;
  if (!Number.isFinite(n)) return 'No deposit';
  return offer.deposit_type === 'percent'
    ? `${n}% deposit`
    : `${money(n, offer.currency)} deposit ex GST`;
}

type TermsMode = 'document' | 'external';

function emptyForm() {
  return {
    cultivar_id: null as number | null,
    rootstock_cultivar_id: null as number | null,
    tree_type: '',
    season_year: String(new Date().getFullYear() + 1),
    order_window_start: '',
    order_window_end: '',
    expected_delivery_start: '',
    expected_delivery_end: '',
    unit_price: '',
    min_order_quantity: '1',
    available_quantity: '',
    deposit_type: 'none' as 'none' | 'percent' | 'fixed',
    deposit_value: '',
    payment_terms_days: '',
    payment_schedule: '',
    terms_mode: 'document' as TermsMode,
    terms_document_id: '',
    acknowledge_external_terms: false,
    // Starts with a scion row: it is the one that is always required.
    material_sources: [blankMaterialSource('scion')] as MaterialSourceInput[],
    status: 'draft' as 'draft' | 'active',
  };
}

export default function NurseryOffers() {
  const { hasAccess: canRead, loading: accessLoading } = useModuleLicense('cultivar', ['viewer', 'pbr', 'admin']);
  const { hasAccess: canWrite } = useModuleLicense('cultivar', ['pbr', 'admin']);
  const orgTz = useOrgTimezone();

  const [offers, setOffers] = useState<NurseryOffer[]>([]);
  const [docs, setDocs] = useState<NurseryTermsDocument[]>([]);
  const [admitted, setAdmitted] = useState<PropagatorTerms[]>([]);
  const [ackText, setAckText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadVersion, setUploadVersion] = useState('');
  const [uploading, setUploading] = useState(false);

  const activeDocs = useMemo(() => docs.filter(d => d.status === 'active'), [docs]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [offersRes, docsRes, termsRes, ackRes] = await Promise.all([
        cultivarService.listNurseryOffers(),
        cultivarService.listNurseryTermsDocuments(),
        cultivarService.listMyPropagatorTerms(),
        cultivarService.getExternalTermsAcknowledgement(),
      ]);
      if (offersRes.success) setOffers(offersRes.data || []);
      if (docsRes.success) setDocs(docsRes.data || []);
      if (termsRes.success) setAdmitted(termsRes.data || []);
      if (ackRes.success) setAckText(ackRes.data?.text || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your offers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!uploadFile || !uploadTitle.trim()) {
      setError('A file and a title are both needed');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      await cultivarService.uploadNurseryTermsDocument(
        uploadFile, uploadTitle.trim(), uploadVersion.trim() || undefined
      );
      setUploadFile(null);
      setUploadTitle('');
      setUploadVersion('');
      setInfo('Terms uploaded. Attach them to an offer to put it on sale.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openTerms = async (doc: NurseryTermsDocument) => {
    setError(null);
    try {
      await cultivarService.openNurseryTermsDocument(doc);
    } catch {
      setError(`Could not open ${doc.original_filename || doc.title}. Try again.`);
    }
  };

  const supersede = async (doc: NurseryTermsDocument) => {
    setError(null);
    try {
      const res = await cultivarService.supersedeNurseryTermsDocument(doc.id);
      if (!res.success) throw new Error(res.error?.message || 'Could not supersede');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not supersede');
    }
  };

  const save = async () => {
    if (!form.cultivar_id) { setError('Pick the cultivar this offer is for'); return; }
    if (form.unit_price.trim() === '') { setError('Set your price per tree'); return; }

    // Said here as well as on the server, so a nursery gets the reason at the
    // moment it matters rather than after a round trip.
    if (form.status === 'active' && form.terms_mode === 'document' && !form.terms_document_id) {
      setError(
        'An offer cannot go on sale without your terms of sale attached — supplying the trees is ' +
        'your contract with the grower. Attach them, or confirm you handle them outside Factree.'
      );
      return;
    }
    if (form.terms_mode === 'external' && !form.acknowledge_external_terms) {
      setError(
        'Tick the acknowledgement. Handling your terms outside Factree makes the supply of the ' +
        'trees your responsibility, and it is recorded against your name.'
      );
      return;
    }
    const materialProblem = materialSourcesProblem(form.material_sources, { requireScion: form.status === 'active' });
    if (materialProblem) { setError(materialProblem); return; }

    setSaving(true);
    setError(null);
    try {
      const external = form.terms_mode === 'external';
      const input: NurseryOfferInput = {
        cultivar_id: form.cultivar_id,
        rootstock_cultivar_id: form.rootstock_cultivar_id,
        tree_type: form.tree_type.trim() || null,
        season_year: Number(form.season_year),
        order_window_start: form.order_window_start || null,
        order_window_end: form.order_window_end || null,
        expected_delivery_start: form.expected_delivery_start || null,
        expected_delivery_end: form.expected_delivery_end || null,
        unit_price: Number(form.unit_price),
        min_order_quantity: Number(form.min_order_quantity) || 1,
        available_quantity: form.available_quantity.trim() === '' ? null : Number(form.available_quantity),
        deposit_type: form.deposit_type,
        deposit_value: form.deposit_type === 'none' || form.deposit_value.trim() === ''
          ? null
          : Number(form.deposit_value),
        payment_terms_days: form.payment_terms_days.trim() === '' ? null : Number(form.payment_terms_days),
        payment_schedule: form.payment_schedule.trim() || null,
        terms_document_id: !external && form.terms_document_id ? Number(form.terms_document_id) : null,
        terms_handled_externally: external,
        acknowledge_external_terms: external && form.acknowledge_external_terms,
        material_sources: form.material_sources.map(s => ({
          ...s,
          source_name: s.source_name.trim(),
          source_reference: (s.source_reference || '').trim() || null,
          notes: (s.notes || '').trim() || null,
        })),
        status: form.status,
      };
      const res = await cultivarService.saveNurseryOffer(input);
      if (!res.success) throw new Error(res.error?.message || 'Could not save the offer');
      setForm(emptyForm());
      setShowForm(false);
      setInfo('Offer saved.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the offer');
    } finally {
      setSaving(false);
    }
  };

  const withdraw = async (offer: NurseryOffer) => {
    setError(null);
    try {
      const res = await cultivarService.withdrawNurseryOffer(offer.id);
      if (!res.success) throw new Error(res.error?.message || 'Could not withdraw');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not withdraw');
    }
  };

  if (accessLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6">
          <h3 className="font-semibold">Cultivar seat required</h3>
          <p className="text-sm text-neutral-600">A cultivar licence seat is needed to manage your offers.</p>
        </CardContent></Card>
      </div>
    );
  }

  const termsMissingForSale =
    form.status === 'active' &&
    ((form.terms_mode === 'document' && !form.terms_document_id) ||
      (form.terms_mode === 'external' && !form.acknowledge_external_terms));

  return (
    <>
      <PageHeader
        title="Your offers"
        subtitle="What you are offering to grow, for which season, and on what terms"
        icon={<Sprout className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4">
        <Card>
          <CardContent className="p-4 space-y-1 text-sm text-neutral-600">
            <p className="flex items-start gap-2">
              <Lock className="h-4 w-4 mt-0.5 flex-shrink-0 text-neutral-400" />
              <span>
                Your tree price is yours. No other nursery sees it, and a grower is shown a single
                combined total with the licence fees folded in — never the parts.
              </span>
            </p>
            <p className="text-neutral-500 pl-6">
              Your terms of sale are your contract with the grower. Factree licenses the cultivar and
              carries your document; it is not a party to the sale of the trees. If you would rather
              handle your terms entirely outside Factree you can — say so on the offer, and that
              acknowledgement is recorded.
            </p>
          </CardContent>
        </Card>

        {error && (
          <div className="flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 rounded p-3">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="break-words">{error}</span>
          </div>
        )}
        {info && (
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-3">{info}</div>
        )}

        {/* --- Terms of sale --- */}
        <Card>
          <CardHeader><CardTitle>Your terms of sale</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {activeDocs.length === 0 ? (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                You have no terms uploaded. An offer cannot go on sale until it has a set attached, or
                you confirm you handle them outside Factree — a grower buying trees needs a contract with you.
              </p>
            ) : (
              <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100">
                {activeDocs.map(doc => (
                  <div key={doc.id} className="p-3 flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-neutral-900 break-words">
                          {doc.title}{doc.version ? ` — v${doc.version}` : ''}
                        </span>
                        <span className="block text-xs text-neutral-500 break-words">
                          {doc.original_filename} · uploaded {formatDateInTz(doc.created_at, orgTz)}
                        </span>
                      </span>
                    </span>
                    <span className="flex items-center gap-3 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => openTerms(doc)}
                        className="text-xs text-blue-600 underline inline-flex items-center gap-1"
                      >
                        Open <ExternalLink className="h-3 w-3" />
                      </button>
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => supersede(doc)}
                          className="text-xs text-neutral-500 underline"
                        >
                          Supersede
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {canWrite && (
              <div className="border border-neutral-200 rounded-lg p-3 bg-neutral-50 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label htmlFor="terms-title" className="block text-xs font-semibold text-neutral-700">Title</label>
                    <input
                      id="terms-title"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Conditions of sale 2027"
                      value={uploadTitle}
                      onChange={e => setUploadTitle(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="terms-version" className="block text-xs font-semibold text-neutral-700">Version (optional)</label>
                    <input
                      id="terms-version"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="2.1"
                      value={uploadVersion}
                      onChange={e => setUploadVersion(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label htmlFor="terms-file" className="block text-xs font-semibold text-neutral-700">Document</label>
                  <input
                    id="terms-file"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="w-full text-sm"
                    onChange={e => setUploadFile(e.target.files?.[0] || null)}
                  />
                </div>
                <Button variant="outline" size="sm" onClick={upload} disabled={uploading}>
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  <span className="ml-1.5">Upload terms</span>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* --- Offers --- */}
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-2">
            <CardTitle>Offers</CardTitle>
            {canWrite && !showForm && (
              <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
                <Plus className="h-4 w-4" /><span className="ml-1.5">New offer</span>
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {admitted.length === 0 && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                You have not been admitted to propagate any cultivar yet. The rights holder decides
                who may, and on what terms — until then there is nothing to offer.
              </p>
            )}

            {showForm && (
              <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50 space-y-4">
                <CultivarPicker
                  label="Cultivar *"
                  hint="One of the cultivars you have been admitted to propagate."
                  placeholder="Search cultivars..."
                  value={form.cultivar_id}
                  onChange={(c: PickedCultivar | null) => setForm(f => ({ ...f, cultivar_id: c ? c.id : null }))}
                />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label htmlFor="offer-tree-type" className="block text-xs font-semibold text-neutral-700">Tree type</label>
                    <input
                      id="offer-tree-type"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Bare root 1yr"
                      value={form.tree_type}
                      onChange={e => setForm(f => ({ ...f, tree_type: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="offer-season" className="block text-xs font-semibold text-neutral-700">Season *</label>
                    <input
                      id="offer-season"
                      type="number"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      value={form.season_year}
                      onChange={e => setForm(f => ({ ...f, season_year: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="offer-price" className="block text-xs font-semibold text-neutral-700">Price per tree, ex GST *</label>
                    <input
                      id="offer-price"
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      value={form.unit_price}
                      onChange={e => setForm(f => ({ ...f, unit_price: e.target.value }))}
                    />
                    <p className="text-xs text-neutral-500">Private to you.</p>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" /> Orders taken between
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="date"
                      aria-label="Order window opens"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      value={form.order_window_start}
                      onChange={e => setForm(f => ({ ...f, order_window_start: e.target.value }))}
                    />
                    <input
                      type="date"
                      aria-label="Order window closes"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      value={form.order_window_end}
                      onChange={e => setForm(f => ({ ...f, order_window_end: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1.5">
                    <Truck className="h-3.5 w-3.5" /> Expected delivery for those orders
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="date"
                      aria-label="Delivery from"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      value={form.expected_delivery_start}
                      onChange={e => setForm(f => ({ ...f, expected_delivery_start: e.target.value }))}
                    />
                    <input
                      type="date"
                      aria-label="Delivery until"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      value={form.expected_delivery_end}
                      onChange={e => setForm(f => ({ ...f, expected_delivery_end: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <label htmlFor="offer-deposit-type" className="block text-xs font-semibold text-neutral-700">Deposit</label>
                    <select
                      id="offer-deposit-type"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      value={form.deposit_type}
                      onChange={e => setForm(f => ({ ...f, deposit_type: e.target.value as typeof f.deposit_type }))}
                    >
                      <option value="none">None</option>
                      <option value="percent">Percentage</option>
                      <option value="fixed">Fixed amount</option>
                    </select>
                  </div>
                  {form.deposit_type !== 'none' && (
                    <div className="space-y-1">
                      <label htmlFor="offer-deposit-value" className="block text-xs font-semibold text-neutral-700">
                        {form.deposit_type === 'percent' ? 'Percent' : 'Amount ex GST'}
                      </label>
                      <input
                        id="offer-deposit-value"
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                        value={form.deposit_value}
                        onChange={e => setForm(f => ({ ...f, deposit_value: e.target.value }))}
                      />
                    </div>
                  )}
                  <div className="space-y-1">
                    <label htmlFor="offer-terms-days" className="block text-xs font-semibold text-neutral-700">Payment terms (days)</label>
                    <input
                      id="offer-terms-days"
                      type="number"
                      min="0"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="30"
                      value={form.payment_terms_days}
                      onChange={e => setForm(f => ({ ...f, payment_terms_days: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label htmlFor="offer-schedule" className="block text-xs font-semibold text-neutral-700">Payment schedule</label>
                  <textarea
                    id="offer-schedule"
                    rows={2}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm resize-none"
                    placeholder="25% on order, balance 14 days before collection"
                    value={form.payment_schedule}
                    onChange={e => setForm(f => ({ ...f, payment_schedule: e.target.value }))}
                  />
                </div>

                {/* How the supply contract is handled. One or the other, never neither. */}
                <fieldset className="space-y-2">
                  <legend className="text-xs font-semibold text-neutral-700 mb-1">Terms of sale for this offer</legend>

                  <label className="flex items-start gap-2 text-sm text-neutral-800">
                    <input
                      type="radio"
                      name="terms-mode"
                      className="mt-1"
                      checked={form.terms_mode === 'document'}
                      onChange={() => setForm(f => ({ ...f, terms_mode: 'document', acknowledge_external_terms: false }))}
                    />
                    <span>Attach our terms of sale</span>
                  </label>
                  {form.terms_mode === 'document' && (
                    <div className="pl-6 space-y-1">
                      <label htmlFor="offer-terms" className="block text-xs font-semibold text-neutral-700">
                        Your terms of sale
                      </label>
                      <select
                        id="offer-terms"
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                        value={form.terms_document_id}
                        onChange={e => setForm(f => ({ ...f, terms_document_id: e.target.value }))}
                      >
                        <option value="">— none attached —</option>
                        {activeDocs.map(d => (
                          <option key={d.id} value={d.id}>
                            {d.title}{d.version ? ` (v${d.version})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <label className="flex items-start gap-2 text-sm text-neutral-800">
                    <input
                      type="radio"
                      name="terms-mode"
                      className="mt-1"
                      checked={form.terms_mode === 'external'}
                      onChange={() => setForm(f => ({ ...f, terms_mode: 'external', terms_document_id: '' }))}
                    />
                    <span>We handle our terms of sale outside Factree</span>
                  </label>
                  {form.terms_mode === 'external' && (
                    <div className="ml-6 border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2">
                      <p className="text-xs text-amber-900 break-words">
                        {ackText || 'Loading the acknowledgement…'}
                      </p>
                      <label className="flex items-start gap-2 text-sm text-amber-950">
                        <input
                          type="checkbox"
                          className="mt-1"
                          disabled={!ackText}
                          checked={form.acknowledge_external_terms}
                          onChange={e => setForm(f => ({ ...f, acknowledge_external_terms: e.target.checked }))}
                        />
                        <span>
                          I acknowledge this on behalf of my organisation. It is recorded against my
                          name, with this wording, at the time I save.
                        </span>
                      </label>
                    </div>
                  )}
                </fieldset>

                <MaterialSourcesEditor
                  value={form.material_sources}
                  onChange={next => setForm(f => ({ ...f, material_sources: next }))}
                  requireScion={form.status === 'active'}
                />

                <div className="space-y-1 sm:max-w-xs">
                  <label htmlFor="offer-status" className="block text-xs font-semibold text-neutral-700">Status</label>
                  <select
                    id="offer-status"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                    value={form.status}
                    onChange={e => setForm(f => ({ ...f, status: e.target.value as 'draft' | 'active' }))}
                  >
                    <option value="draft">Draft — not on sale</option>
                    <option value="active">On sale</option>
                  </select>
                  {termsMissingForSale && (
                    <p className="text-xs text-amber-700">
                      Attach your terms, or confirm you handle them outside Factree — a grower needs a contract with you.
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button variant="primary" size="sm" onClick={save} disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save offer'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setForm(emptyForm()); setShowForm(false); }}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {offers.length === 0 ? (
              <p className="text-sm text-neutral-500">No offers yet.</p>
            ) : (
              <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100">
                {offers.map(offer => {
                  const sources = offer.material_sources || [];
                  return (
                    <div key={offer.id} className="p-3 space-y-2">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-neutral-900 break-words">
                            {offer.cultivar_trade_name
                              ? `${offer.cultivar_name} (${offer.cultivar_trade_name})`
                              : offer.cultivar_name}
                            {offer.tree_type ? ` · ${offer.tree_type}` : ''}
                          </p>
                          <p className="text-xs text-neutral-500">
                            Season {offer.season_year}
                            {offer.rootstock_name ? ` · on ${offer.rootstock_name}` : ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-sm font-semibold tabular-nums">
                            {money(offer.unit_price, offer.currency)} / tree ex GST
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[offer.status]}`}>
                            {offer.status}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs text-neutral-600">
                        <span>
                          Orders{' '}
                          {offer.order_window_start
                            ? formatLocalDateInTz(offer.order_window_start, orgTz)
                            : 'anytime'}
                          {offer.order_window_end ? ` – ${formatLocalDateInTz(offer.order_window_end, orgTz)}` : ''}
                        </span>
                        <span>
                          Delivery{' '}
                          {offer.expected_delivery_start
                            ? formatLocalDateInTz(offer.expected_delivery_start, orgTz)
                            : 'not stated'}
                          {offer.expected_delivery_end ? ` – ${formatLocalDateInTz(offer.expected_delivery_end, orgTz)}` : ''}
                        </span>
                        <span>
                          {depositLabel(offer)}
                          {offer.payment_terms_days !== null ? ` · net ${offer.payment_terms_days}d` : ''}
                        </span>
                      </div>

                      {offer.payment_schedule && (
                        <p className="text-xs text-neutral-600 break-words">{offer.payment_schedule}</p>
                      )}

                      {sources.length > 0 ? (
                        <ul className="text-xs text-neutral-600 space-y-0.5">
                          {sources.map(s => (
                            <li key={s.id} className="break-words">
                              <span className="font-medium">{MATERIAL_KIND_LABELS[s.material_kind]}:</span>{' '}
                              {s.source_name}{s.source_reference ? ` (${s.source_reference})` : ''}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-amber-700">No material source recorded — cannot go on sale</p>
                      )}

                      <div className="flex flex-wrap items-center gap-3">
                        {offer.terms_document_id ? (
                          <span className="text-xs text-neutral-600 inline-flex items-center gap-1">
                            <FileText className="h-3 w-3 flex-shrink-0" />
                            <span className="break-words">
                              {offer.terms_title}{offer.terms_version ? ` v${offer.terms_version}` : ''}
                            </span>
                          </span>
                        ) : offer.terms_handled_externally ? (
                          <span className="text-xs text-neutral-600 inline-flex items-center gap-1">
                            <ShieldCheck className="h-3 w-3 flex-shrink-0" />
                            <span className="break-words">
                              Terms handled outside Factree
                              {offer.external_terms_acknowledged_at
                                ? ` — acknowledged ${formatDateInTz(offer.external_terms_acknowledged_at, orgTz)}`
                                : ''}
                            </span>
                          </span>
                        ) : (
                          <span className="text-xs text-amber-700">No terms attached — cannot go on sale</span>
                        )}
                        {canWrite && offer.status !== 'withdrawn' && (
                          <button
                            type="button"
                            onClick={() => withdraw(offer)}
                            className="text-xs text-red-600 underline inline-flex items-center gap-1"
                          >
                            <X className="h-3 w-3" /> Withdraw
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
